import { useEffect, useState } from 'react';
import type { Color } from '@edh/shared';
import { identityGradient } from '@edh/shared';

/**
 * Spec 8's signature element. The fill is a gradient across the deck's colour
 * identity, so the standings are scannable by colour before you read a single
 * number, and the width is the rating's position within the pool.
 *
 * The scale is deliberately relative. An absolute 0-3000 axis would leave every
 * deck in a small league sitting in an indistinguishable clump around the middle;
 * what the table actually wants to see is who is ahead of whom tonight.
 */

export interface RatingBarProps {
  elo: number;
  min: number;
  max: number;
  colors: readonly Color[];
  /** Provisional ratings render hatched — the number is not real yet. */
  provisional?: boolean;
  /** Animate from a previous value, for the resolve moment. */
  from?: number;
  /**
   * During placement the bar measures games played, not rating.
   *
   * Everyone starts at 1200, so a rating bar for an unplaced deck sat exactly
   * half full and stayed there — a precise-looking answer to a question nobody
   * can answer yet. Progress toward being rated is something it does know.
   */
  placement?: { played: number; needed: number };
}

function pct(elo: number, min: number, max: number): number {
  if (max - min < 1) return 50;
  return Math.max(4, Math.min(100, ((elo - min) / (max - min)) * 100));
}

const progress = (played: number, needed: number): number =>
  (Math.max(0, Math.min(played, needed)) / needed) * 100;

export function RatingBar({
  elo,
  min,
  max,
  colors,
  provisional,
  from,
  placement,
}: RatingBarProps) {
  const target = placement ? progress(placement.played, placement.needed) : pct(elo, min, max);

  /**
   * Where this bar was before the match being shown.
   *
   * A placement bar measures games played rather than rating, so its previous
   * value is one game fewer — not the old Elo, which it was never showing. It
   * used to be excluded from the animation entirely, which meant a league where
   * everyone is still placing had a result screen with nothing on it that moved.
   */
  const start =
    from === undefined
      ? undefined
      : placement
        ? progress(placement.played - 1, placement.needed)
        : pct(from, min, max);

  const [width, setWidth] = useState(() => start ?? target);

  useEffect(() => {
    if (start === undefined) {
      setWidth(target);
      return;
    }
    setWidth(start);
    // Start at the old value, then hand the browser the new one on the next
    // frame so the transition actually has two states to move between.
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setWidth(target)));
    return () => cancelAnimationFrame(id);
  }, [target, start]);

  return (
    <div className={`bar${provisional ? ' provisional' : ''}`}>
      <div
        className="bar-fill"
        style={{ width: `${width}%`, background: identityGradient(colors) }}
      />
    </div>
  );
}

/** The pool range every bar in a list is measured against. */
export function ratingRange(values: number[]): { min: number; max: number } {
  if (values.length === 0) return { min: 1100, max: 1300 };
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  // A little padding so the leader's bar is not pinned to the very edge, and a
  // floor on the span so three decks within 10 points don't look like a chasm.
  const span = Math.max(120, hi - lo);
  const mid = (hi + lo) / 2;
  return { min: mid - span * 0.62, max: mid + span * 0.62 };
}
